import type { PageType } from '@space-shell/common/services/product/enums'
import type {
  SubscriptionPlan,
  CouponInfo
} from '@space-shell/modules/sales/presentation/constants/subscriptions'

import type { SectionProductDetailStatus, SectionType } from './enums'

import type { ShowcaseOrientation } from '../membership'

export type Section = {
  id: string
  title: string
  itemsSize: number
  type: SectionType
  orientation: ShowcaseOrientation
  active: boolean
  entityId?: string
}

export type SectionsParams = {
  size: number
  cursor?: string
  isPreview?: boolean
}

export type SectionsResponse = {
  content: Section[]
  nextCursor?: string
}

export type SectionItem = (Product & Track) | Module

export type SectionItemsResponse = {
  content: SectionItem[]
}

export type Product = {
  title: string
  categoryId?: number
  communityId?: number
  description?: string
  itemId: string
  image?: {
    url?: string
    altText?: string
  }
  avatarImage?: {
    url?: string
    altText?: string
  }
  verticalImage?: {
    url?: string
    altText?: string
  }
  horizontalImage?: {
    url?: string
    altText?: string
  }
  bwImage?: {
    url?: string
  }
  category?: string
  freeSignUp: boolean
  price?: string
  plan?: SubscriptionPlan
  purchased: boolean
  joined: boolean
  productHotLink?: string
  showForSale?: boolean
  accessLink?: string
  affiliationCode?: string
  clubSalesPage?: {
    pageOnline: boolean
    salesEnabled: boolean
    slug: string
  }
  version?: string
  couponInfo?: CouponInfo
  checkoutInfo?: CheckoutInfo
  status?: string
}

export enum PaymentTypes {
  GENERIC = 'GENERIC',
  CREDIT_CARD = 'CREDIT_CARD',
  BILLET = 'BILLET',
  PAYPAL = 'PAYPAL',
  GOOGLE_PAY = 'GOOGLE_PAY',
  SAMSUMG_PAY = 'SAMSUMG_PAY',
  CASH_PAYMENT = 'CASH_PAYMENT'
}

export enum SummaryType {
  COUPON = 'COUPON',
  FULL_PRICE = 'FULL_PRICE',
  TAX_FEDERAL = 'TAX_FEDERAL',
  TAX_PROVINCIAL = 'TAX_PROVINCIAL'
}

export type CheckoutInfo = {
  paymentMethods: PaymentMethod[]
}

export type CheckoutPrice = {
  price?: string | null
  fullPrice: string | null
  discountedPrice?: {
    coupon: number
    fullPrice: string | null
  }
}

export type PaymentMethod = {
  type: PaymentTypes
  amount: Amount
  summary?: Summary
  offer: string
}

export type Amount = {
  value: number
  currency: string
}

type Summary = {
  items: SummaryItem[]
}

type SummaryItem = {
  type: 'COUPON' | 'FULL_PRICE' | 'TAX_FEDERAL' | 'TAX_PROVINCIAL'
  operation: 'ADD' | 'NONE' | 'SUBTRACT' | 'OMIT'
  value: number
  currency: string
  percent: number
}

export type Module = {
  title: string
  name?: string
  description?: string
  itemId: string
  productId: number
  image?: {
    url?: string
  }
  cover?: string
  published: boolean
  publicModule: boolean
  paidModule: boolean
  purchased: boolean
  extra: boolean
  productHotLink?: string
  currentPage?: string
  pageType: PageType
  completedPercentage: number
  purchasedModule: boolean
}

export type Track = {
  id?: string
  itemId?: string
  title: string
  published?: boolean
  image?: {
    url?: string
    altText?: string
  }
}

export type SectionProductDetail = {
  id: number
  name: string
  image?: {
    url?: string
    altText?: string
  }
  category?: string
  freeSignUp?: boolean
  price?: string
  plan?: SubscriptionPlan
  purchased?: boolean
  joined?: boolean
  productHotLink?: string
  accessLink?: string
  status?: SectionProductDetailStatus
  showForSale?: boolean
  couponInfo?: CouponInfo
  checkoutInfo?: CheckoutInfo
}
